import { useEffect, useRef } from 'react';

import queryString from 'query-string';
import { CSVLink } from 'react-csv';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { Flex, Box } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import i18n from '~/i18n';
import { colors } from '~/styles/colors';

import {
  Button, DataTable, Loader, StatusBox, HealthIcon,
} from '~/components';
import { healthLevelDesc } from '~/components/HealthIcon';
import { getUserProfile } from '~/helpers/userProfile';
import { useStateVar } from '~/helpers/useStateVar';
import { saveSessionStorageItem, removeSessionStorageItem, getSessionStorageItem } from '~/helpers/cachedStorage';
import { apiCall, ApiParams } from '~/providers';
import { AnalysisEmpty } from '../../AnalysisEmpty';

import {
  WrapperDesktop,
  WrapperMobile,
  StyledLink,
  StyledSpan,
} from './styles';

import '~/assets/css/ReactTags.css';
import { TableColumn, OrderableHeaderCell } from '~/components/DataTable';
import { FilterItem } from '../../AnalysisFilters';
import { UtilFilter } from '../../Utilities/UtilityFilter';
import { ajustParams, setValueState } from '~/helpers/genericHelper';
import { withTransaction } from '@elastic/apm-rum-react';
import { formatNumberWithFractionDigits } from '~/helpers/thousandFormatNumber';

const t = i18n.t.bind(i18n);

const CSVheader = [
  { label: t('cliente'), key: 'CLIENT_NAME' },
  { label: t('estado'), key: 'state' },
  { label: t('cidade'), key: 'city' },
  { label: t('unidade'), key: 'unit' },
  { label: t('maquina'), key: 'machine' },
  { label: t('ativo'), key: 'asset' },
  { label: t('dispositivo'), key: 'device' },
  { label: t('saude'), key: 'health' },
  { label: t('ultimaAlteracaoDaSaude'), key: 'healthDate' },
  { label: t('descricaoSaude'), key: 'healthDesc' },
  { label: t('conexao'), key: 'status' },
  { label: t('ultimoVisto'), key: 'lastCommTs' },
  { label: t('fluido'), key: 'fluidType' },
  { label: t('aplicacaoMaquina'), key: 'appl' },
  { label: t('tipoMaquina'), key: 'type' },
  { label: t('variavelP0'), key: 'p0var' },
  { label: t('tipoSensorP0'), key: 'p0sensor' },
  { label: t('variavelP1'), key: 'p1var' },
  { label: t('tipoSensorP1'), key: 'p1sensor' },
  { label: t('capacidadeKw'), key: 'capkw' },
  { label: t('temperaturaAmbienteC'), key: 'Tamb' },
  { label: t('temperaturaLiquidoC'), key: 'Tliq' },
  { label: t('temperaturaSuccaoC'), key: 'Tsuc' },
  { label: 'P0 (ADC)', key: 'P0Raw' },
  { label: 'P1 (ADC)', key: 'P1Raw' },
  { label: t('temperaturaRetorno'), key: 'tempRetorno' },
  { label: t('temperaturaInsuflacao'), key: 'tempInsuflacao' },
  { label: t('offsetTemperatura'), key: 'tempOffset' },
];

const healthCodes = {
  'Equipamento offline': 2,
  'Máquina desativada': 4,
  'Manutenção urgente': 25,
  'Risco iminente': 50,
  'Fora de especificação': 75,
  'Operando corretamente': 100,
};

export const Machines = ({ history }): JSX.Element => {
  const { t } = useTranslation();
  const csvLinkEl = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);
  const profile = getUserProfile();
  function formattedFilter(preFiltered: string | string[] | null) {
    let formattedPreFilter = [] as string[];
    if (preFiltered === 'Sem informação') {
      formattedPreFilter.push('Máquina desativada');
      formattedPreFilter.push('Equipamento offline');
    } else if (preFiltered?.includes('Sem informação') && typeof preFiltered !== 'string') {
      formattedPreFilter = preFiltered.filter((x) => x !== 'Sem informação');
      formattedPreFilter.push('Máquina desativada');
      formattedPreFilter.push('Equipamento offline');
    } else if (typeof preFiltered === 'string') {
      formattedPreFilter = preFiltered ? [preFiltered] : [];
    }
    return formattedPreFilter;
  }
  const [state, render] = useStateVar(() => {
    const {
      preFiltered,
    } = queryString.parse(history.location.search, { arrayFormat: 'comma' });
    const formattedPreFilter = formattedFilter(preFiltered);

    return {
      actionOpts: [] as {}[],
      columnsMobile: [] as TableColumn[],
      columnsDesktop: [] as TableColumn[],
      list: [] as {
        CLIENT_NAME: string
        STATE_ID: string
        CITY_NAME: string
        UNIT_ID: number
        UNIT_NAME: string
        MACHINE_ID: number
        MACHINE_NAME: string
        ASSET_ID?: number
        AST_DESC?: string
        DAT_ID?: string
        DEVICE_CODE?: string
        H_INDEX?: number
        H_DESC?: string
        status?: string
        lastCommTs?: string
      }[],
      selectedClientFilter: setValueState('filterClient'),
      isLoading: !profile.manageAllClients && !profile.manageSomeClient,
      csvData: [] as {}[],
      searchState: '' as string,
      profile,
      tablePage: 1,
      tablePageSize: 50,
      totalItems: 0,
      orderBy: null as null | [string, 'ASC' | 'DESC'],

      statesList: [] as { value: string, name: string }[],
      selectedState: setValueState('filterStates'),
      citiesList: [] as { value: string, name: string }[],
      selectedCity: setValueState('filterCity'),
      unitsList: [] as { value: string, name: string }[],
      selectedUnit: setValueState('filterUnit'),
      selectedHealth: formattedPreFilter.map((code) => healthCodes[code]),
      selectedConnection: [],
      selectedDevId: '',
      filters: [] as FilterItem[],
      needFilter: profile.manageAllClients || profile.manageSomeClient,
    };
  });

  useEffect(() => {
    if (profile.permissions.isAdminSistema) {
      state.actionOpts = [{ label: 'Excluir', value: 'delete' }, { label: 'Atribuir Cliente', value: 'set-client' }];
    } else if (profile.manageAllClients) {
      state.actionOpts = [{ label: 'Atribuir Cliente', value: 'set-client' }];
    }
    populateColumnsDesktop();
    populateColumnsMobile();
    verifyPermissionsAndPopulateColumns();
  }, []);

  function cellMachine(MACHINE_NAME: string, MACHINE_ID: number) {
    const label = MACHINE_NAME?.length > 100 ? MACHINE_NAME.slice(0, 100).concat('...') : MACHINE_NAME;
    if (!MACHINE_ID) return label ? <StyledSpan>{label}</StyledSpan> : <>-</>;
    return (
      <StyledLink to={`/analise/maquina/${MACHINE_ID}/ativos`}>
        <StyledSpan>{label || '-'}</StyledSpan>
      </StyledLink>
    );
  }

  function cellAsset(AST_DESC: string, DAT_ID: string, ASSET_ID: number) {
    const label = AST_DESC?.length > 50 ? AST_DESC.slice(0, 50).concat('...') : AST_DESC;
    const urlKey = DAT_ID || ASSET_ID;
    if (!urlKey) return <>-</>;
    return (
      <StyledLink to={`/analise/ativo/${urlKey}/informacoes`}>
        <StyledSpan>{label || '-'}</StyledSpan>
      </StyledLink>
    );
  }

  function cellDevice(DEVICE_CODE?: string, DAT_ID?: string, ASSET_ID?: number) {
    if (DEVICE_CODE) {
      return (
        <StyledLink to={`/analise/dispositivo/${DEVICE_CODE}/informacoes`}>
          <StyledSpan>{DEVICE_CODE}</StyledSpan>
        </StyledLink>
      );
    }
    const assetKey = DAT_ID || ASSET_ID;
    if (assetKey) {
      return (
        <StyledLink to={`/analise/ativo/${assetKey}/informacoes`}>
          <StyledSpan>-</StyledSpan>
        </StyledLink>
      );
    }
    return <>-</>;
  }

  function orderColumn(column: TableColumn) {
    return (
      <OrderableHeaderCell onColumnClick={onColumnClick} column={column} orderBy={state.orderBy} />
    );
  }

  function populateColumnsDesktop() {
    if (profile.viewMultipleClients) {
      state.columnsDesktop.push({
        Header: t('cliente'),
        accessor: 'col_CLIENT_NAME',
        Cell: (props) => (props.CLIENT_NAME ? <StyledSpan>{props.CLIENT_NAME}</StyledSpan> : '-'),
        HeaderCell: orderColumn,
      });
    }

    state.columnsDesktop.push({
      Header: t('estado'),
      accessor: 'col_STATE_ID',
      Cell: (props) => (props.STATE_ID ? <StyledSpan>{props.STATE_ID}</StyledSpan> : '-'),
      HeaderCell: orderColumn,
    });

    state.columnsDesktop.push({
      Header: t('cidade'),
      accessor: 'col_CITY_NAME',
      Cell: (props) => (props.CITY_NAME ? <StyledSpan>{props.CITY_NAME}</StyledSpan> : '-'),
      HeaderCell: orderColumn,
    });

    state.columnsDesktop.push({
      Header: t('unidade'),
      accessor: 'col_UNIT_NAME',
      Cell: (props) => (props.UNIT_ID
        ? <StyledLink to={`/analise/unidades/${props.UNIT_ID}`}><StyledSpan onClick={saveSessionFilters}>{props.UNIT_NAME}</StyledSpan></StyledLink> : '-'),
      HeaderCell: orderColumn,
    });

    state.columnsDesktop.push({
      Header: t('maquina'),
      accessor: 'col_MACHINE_NAME',
      Cell: (props) => <StyledSpan onClick={saveSessionFilters}>{cellMachine(props.MACHINE_NAME, props.MACHINE_ID)}</StyledSpan>,
      HeaderCell: orderColumn,
    });

    state.columnsDesktop.push({
      Header: t('ativo'),
      accessor: 'col_DAT_ID',
      Cell: (props) => <StyledSpan onClick={saveSessionFilters}>{cellAsset(props.AST_DESC, props.DAT_ID, props.ASSET_ID)}</StyledSpan>,
      HeaderCell: orderColumn,
    });

    state.columnsDesktop.push({
      Header: t('dispositivo'),
      accessor: 'col_DEV_ID',
      Cell: (props) => <StyledSpan onClick={saveSessionFilters}>{cellDevice(props.DEVICE_CODE, props.DAT_ID, props.ASSET_ID)}</StyledSpan>,
      HeaderCell: orderColumn,
    });

    state.columnsDesktop.push({
      Header: t('saude'),
      accessor: 'col_H_INDEX',
      Cell: (props) => (props.H_INDEX != null ? <HealthIcon health={String(props.H_INDEX)} label={props.col_H_DESC} /> : '-'),
      HeaderCell: orderColumn,
    });

    state.columnsDesktop.push({
      Header: t('conexao'),
      accessor: 'col_status',
      Cell: (props) => (props.status ? <StatusBox status={props.status}>{props.status}</StatusBox> : '-'),
      HeaderCell: orderColumn,
    });
  }

  function populateColumnsMobile() {
    state.columnsMobile.push({
      Header: t('ativo'),
      accessor: 'col_DAT_ID',
      Cell: (props) => <StyledSpan onClick={saveSessionFilters}>{cellAsset(props.AST_DESC, props.DAT_ID, props.ASSET_ID)}</StyledSpan>,
    });

    state.columnsMobile.push({
      Header: t('dispositivo'),
      accessor: 'col_DEV_ID',
      Cell: (props) => <StyledSpan onClick={saveSessionFilters}>{cellDevice(props.DEVICE_CODE, props.DAT_ID, props.ASSET_ID)}</StyledSpan>,
    });

    state.columnsMobile.push({
      Header: t('saude'),
      accessor: 'col_H_INDEX',
      Cell: (props) => (props.H_INDEX != null ? <HealthIcon health={String(props.H_INDEX)} label={props.col_H_DESC} /> : '-'),
    });

    state.columnsMobile.push({
      Header: t('conexao'),
      accessor: 'col_status',
      Cell: (props) => (props.status ? <StatusBox status={props.status}>{props.status}</StatusBox> : '-'),
    });
  }

  function verifyPermissionsAndPopulateColumns() {
    if (profile.manageAllClients) {
      state.columnsDesktop.push({
        Header: t('ultimaVezVisto'),
        accessor: 'col_lastCommTs',
        Cell: (props) => (props.lastCommTs || '-'),
        HeaderCell: orderColumn,
      });
    }
  }

  function loadSessionFilters() {
    const filters = getSessionStorageItem('machinesAnalysisFilters') as null | {
      searchState: '',
      selectedState: [],
      selectedCity: [],
      selectedUnit: [],
      selectedClientFilter?: [],
      selectedHealth: [],
      selectedConnection: [],
    };
    if (filters) {
      state.searchState = filters.searchState || '';
      state.selectedState = filters.selectedState || [];
      state.selectedCity = filters.selectedCity || [];
      state.selectedUnit = filters.selectedUnit || [];
      state.selectedClientFilter = filters.selectedClientFilter || [];
      state.selectedHealth = filters.selectedHealth || [];
      state.selectedConnection = filters.selectedConnection || [];
    }
    render();
    removeSessionStorageItem('machinesAnalysisFilters');
  }

  function saveSessionFilters() {
    const filters = {
      searchState: state.searchState,
      selectedState: state.selectedState,
      selectedCity: state.selectedCity,
      selectedUnit: state.selectedUnit,
      selectedHealth: state.selectedHealth,
      selectedConnection: state.selectedConnection,
      selectedClientFilter: null as null|({}[]),
    };
    if (profile.manageAllClients) {
      filters.selectedClientFilter = state.selectedClientFilter;
    }
    saveSessionStorageItem('machinesAnalysisFilters', filters);
  }

  async function clearFilters() {
    state.searchState = '';
    state.selectedCity = [];
    state.selectedClientFilter = [];
    state.selectedState = [];
    state.selectedConnection = [];
    state.selectedUnit = [];
    state.selectedHealth = [];
    state.isLoading = true;
    render();
    await handleGetData();
  }

  function buildApiParams(isExport?: boolean): ApiParams['/machines/get-machines-assets-and-devices'] {
    return {
      INCLUDE_INSTALLATION_UNIT: !!profile.viewAllClients || !!profile.permissions.isInstaller,
      SKIP: isExport ? 0 : (state.tablePage - 1) * state.tablePageSize,
      LIMIT: isExport ? undefined : state.tablePageSize,
      searchTerms: state.selectedDevId ? [state.selectedDevId.toLowerCase()] : [],
      orderByProp: state.orderBy?.[0]?.substring('col_'.length) || undefined,
      orderByDesc: (state.orderBy?.[1] === 'DESC') || undefined,
      stateIds: state.selectedState.length > 0 ? ajustParams(state.selectedState) : undefined,
      cityIds: state.selectedCity.length > 0 ? ajustParams(state.selectedCity) : undefined,
      unitIds: state.selectedUnit.length > 0 ? ajustParams(state.selectedUnit) : undefined,
      clientIds: state.selectedClientFilter.length > 0 ? ajustParams(state.selectedClientFilter) : undefined,
      healthIndexes: state.selectedHealth.length > 0 ? state.selectedHealth : undefined,
      status: state.selectedConnection.length > 0 ? state.selectedConnection : undefined,
      searchMachine: state.searchState || undefined,
      includeExportColumns: !!isExport,
    };
  }

  async function handleGetData() {
    try {
      state.isLoading = true; render();
      if (state.needFilter !== null) state.needFilter = false;
      loadSessionFilters();

      const { list, totalItems } = await apiCall('/machines/get-machines-assets-and-devices', buildApiParams());

      state.list = list.map((item) => ({
        ...item,
        STATE_ID: item.STATE_ID || '-',
        CITY_NAME: item.CITY_NAME || '-',
        UNIT_NAME: item.UNIT_NAME || '-',
        MACHINE_NAME: item.MACHINE_NAME || '-',
        col_H_DESC: healthLevelDesc[String(item.H_INDEX)] || item.H_DESC || 'Sem informação',
      }));
      state.totalItems = totalItems;
      render();
    } catch (err) {
      console.log(err);
      toast.error(t('erroDadosMaquinas'));
    }
    if (state.needFilter === null) state.needFilter = true;
    state.isLoading = false; render();
  }

  const awaitData = async () => {
    try {
      await handleGetData();
    } catch (error) {
      console.log(error); toast.error(t('houveErro'));
    }
  };

  useEffect(() => {
    state.tablePage = 1;
    if (!profile.manageAllClients && !profile.manageSomeClient) {
      awaitData();
    }
  }, []);

  const onPageChange = (page) => {
    state.tablePage = page;
    render();
    handleGetData();
  };

  function onColumnClick(column: { accessor: string }) {
    if (state.orderBy && state.orderBy[1] === 'ASC' && state.orderBy[0] === column.accessor) {
      state.orderBy = [column.accessor, 'DESC'];
    } else {
      state.orderBy = [column.accessor, 'ASC'];
    }
    render();
    handleGetData();
  }

  function verifyExist(item) {
    return item || '-';
  }

  const getCsvData = async () => {
    state.isLoading = true; render();
    try {
      const { list } = await apiCall('/machines/get-machines-assets-and-devices', buildApiParams(true));

      state.csvData = list.map((item) => ({
        CLIENT_NAME: verifyExist(item.CLIENT_NAME),
        state: verifyExist(item.STATE_ID),
        city: verifyExist(item.CITY_NAME),
        unit: verifyExist(item.UNIT_NAME),
        machine: verifyExist(item.MACHINE_NAME),
        asset: verifyExist(item.AST_DESC),
        device: verifyExist(item.DEVICE_CODE),
        health: healthLevelDesc[String(item.H_INDEX)] || item.H_DESC || '-',
        healthDate: verifyExist(item.H_DATE),
        healthDesc: verifyExist(item.H_DESC),
        status: verifyExist(item.status),
        lastCommTs: verifyExist(item.lastCommTs),
        fluidType: verifyExist(item.FLUID_TYPE),
        appl: verifyExist(item.MACHINE_APPLICATION),
        type: verifyExist(item.MACHINE_TYPE),
        p0var: verifyExist(item.P0_POSITN),
        p0sensor: verifyExist(item.P0_SENSOR),
        p1var: verifyExist(item.P1_POSITN),
        p1sensor: verifyExist(item.P1_SENSOR),
        tempRetorno: verifyExist(item.Temperature != null ? formatNumberWithFractionDigits(item.Temperature) : undefined),
        tempInsuflacao: verifyExist(item.Temperature_1 != null ? formatNumberWithFractionDigits(item.Temperature_1) : undefined),
        tempOffset: verifyExist(item.TEMPERATURE_OFFSET != null ? String(item.TEMPERATURE_OFFSET) : undefined),
        capkw: verifyExist(item.capacityKW),
        Tamb: verifyExist(item.Tamb),
        Tliq: verifyExist(item.Tliq),
        Tsuc: verifyExist(item.Tsuc),
        P0Raw: verifyExist(item.P0Raw),
        P1Raw: verifyExist(item.P1Raw),
      }));
      render();
      setTimeout(() => { csvLinkEl.current?.link.click(); }, 1000);
    } catch (err) { console.log(err); toast.error(t('houveErro')); }
    state.isLoading = false; render();
  };

  return (
    <>
      <Helmet>
        <title>{t('dielEnergiaMaquinas')}</title>
      </Helmet>
      <UtilFilter
        state={state}
        render={render}
        onAply={() => { state.tablePage = 1; handleGetData(); }}
        clearFilters={clearFilters}
        exportFunc={getCsvData}
        csvHeader={CSVheader}
        listFilters={['conexao', 'saude', 'estado', 'cidade', 'cliente', 'unidade', 'id', 'search']}
        searchLabel={t('maquinas')}
        lengthArrayResult={state.list.length}
      />
      <CSVLink
        headers={CSVheader}
        data={state.csvData}
        separator=";"
        enclosingCharacter={"'"}
        filename={t('ListagemDeMaquinasCsv')}
        asyncOnClick
        ref={csvLinkEl}
      />
      <Flex flexWrap="wrap">
        <Box width={1} paddingTop={3}>
          <WrapperDesktop>
            {state.needFilter && (
              <>
                <AnalysisEmpty />
              </>
            )}
            {state.isLoading
              ? <Loader variant="primary" />
              : !state.needFilter && (
                <DataTable
                  isUnit={false}
                  columns={state.columnsDesktop}
                  data={state.list}
                  onPageChange={onPageChange}
                  currentPage={state.tablePage}
                  pageSize={state.tablePageSize}
                  totalItems={state.totalItems}
                />
              )}
          </WrapperDesktop>
          <WrapperMobile>
            {state.isLoading
              ? <Loader variant="primary" />
              : (
                <DataTable
                  isUnit={false}
                  columns={state.columnsMobile}
                  data={state.list}
                  onPageChange={onPageChange}
                  currentPage={state.tablePage}
                  pageSize={state.tablePageSize}
                  totalItems={state.totalItems}
                />
              )}
          </WrapperMobile>
        </Box>
      </Flex>
    </>
  );
};

const StatusIcon = styled.div<{ color?, status?}>(
  ({ color, status }) => `
  width: 10px;
  height: 10px;
  margin-left: 5px;
  border-radius: 50%;
  border: 2px solid ${color || (status === 'ONLINE' ? colors.Blue300 : colors.Grey200)};
  background: ${color || (status === 'ONLINE' ? colors.Blue300 : colors.Grey200)};
  font-weight: bold;
  font-size: 0.8em;
  line-height: 18px;
  color: ${colors.White};
  display: flex;
  justify-content: center;
  align-items: center;
  text-transform: capitalize;
`,
);

export default withTransaction('Machines', 'component')(Machines);
